import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {BdDunningDto} from '../../../model/dto/api/bd-dunning.dto';
import {
    BdCreateVirtualPlanRequest,
    DunningPaymentPlanService
} from '../../../service/api/dunning-payment-plan.service';
import {BdDecisionDataService} from '../../../service/api/bd-decision-data.service';
import {BdDecisionContextDto} from '../../../model/dto/api/bd-decision-context.dto';
import {
    BdRateAgreementInstallment,
    BdRateInterval,
    computeRatePlan
} from '../../../util/bd-rate-plan.util';

/**
 * Auswahl-Option fuer das Raten-Intervall.
 */
interface BdRateIntervalOption {
    label: string;
    value: BdRateInterval;
}

/**
 * AI:
 * Status: 'wave-5.1-implementation'
 * Type: Component
 * SubType: Exit-UI / Rate-Agreement-Dialog
 * Reason: Pending-Action-Dialog fuer die Anlage einer Ratenvereinbarung. Erfasst
 *         Raten-Anzahl (2-12), Start-Datum, Intervall und berechnet automatisch
 *         den Raten-Betrag sowie die Faelligkeitsdaten als Vorschau-Tabelle.
 *
 *         Wave 5.1: confirm() ruft jetzt
 *         {@link DunningPaymentPlanService.createVirtualPlan} mit dunningId,
 *         rates und dueDates auf. Bei Erfolg wird actionCompleted ausgesendet,
 *         bei Fehler wird die Fehlermeldung im Dialog angezeigt.
 *
 *         Mapped to {@link BdDunningExitUiComponent.RATE_AGREEMENT_DIALOG}.
 *
 * @ticket TM-1000001782
 */
@Component({
    selector: 'mvs-bd-rate-agreement-dialog',
    templateUrl: './bd-rate-agreement-dialog.component.html',
    styleUrls: ['./bd-rate-agreement-dialog.component.scss'],
    standalone: false
})
export class BdRateAgreementDialogComponent implements OnInit, OnChanges, OnDestroy {

    // ------------------------------------------------------------------ //
    // Inputs
    // ------------------------------------------------------------------ //

    @Input() dunning: BdDunningDto | null = null;
    /** A2: Id der Mahnung — triggert das Laden des Entscheidungs-Kontextes. */
    @Input() dunningId: number | null = null;
    @Input() uiComponentConfig: string | null = null;

    // ------------------------------------------------------------------ //
    // U17: Vom Drawer (ngComponentOutletInputs) durchgereichte Sub-Daten.
    // Diese Komponente verwendet sie aktuell nicht, MUSS sie aber als @Input()
    // deklarieren, sonst meldet Angular NG0303 ("Can't bind to '…' since it
    // isn't a known property") für jede der fünf Bindungen. Optional gehalten.
    // ------------------------------------------------------------------ //

    /** U17: Liste der Verträge der Mahnung (vom Drawer durchgereicht). */
    @Input() contracts: unknown[] | null = null;

    /** U17: Liste der Inkasso-Übergaben der Mahnung. */
    @Input() handOvers: unknown[] | null = null;

    /** U17: Aktive Mahn-Pause, sofern vorhanden. */
    @Input() activePause: unknown | null = null;

    /** U17: Gebrochener Zahlungsplan, sofern vorhanden. */
    @Input() breachedPlan: unknown | null = null;

    /** U17: resolved id of the current user (passed through by the drawer; currently unused). */
    @Input() currentUserId: number | null = null;

    // ------------------------------------------------------------------ //
    // Outputs
    // ------------------------------------------------------------------ //

    @Output() actionCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() actionCancelled: EventEmitter<void> = new EventEmitter<void>();

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized: boolean = false;
    visible: boolean = true;
    submitting: boolean = false;
    errorMessage: string | null = null;

    /** Anzeige-Felder fuer den Mahnungs-Header. */
    customerName: string = '—';
    dunningTypeName: string = '—';
    activeLevelLabel: string = '—';

    /**
     * A2: true, sobald der Entscheidungs-Kontext (Backend) Kunde/Stufe gesetzt hat.
     * Verhindert, dass ein spaeter feuerndes {@link initComponent} (ngOnChanges des
     * Dunning-Inputs) die reichen Klartext-Werte mit den duennen Worklist-Werten
     * ('—') ueberschreibt.
     */
    private contextApplied: boolean = false;

    /**
     * A2: Mahn-relevanter Saldo (Heute-Sicht) aus dem Entscheidungs-Kontext.
     * Dient nur der Information im Kopf; der Gesamtbetrag der Raten bleibt der
     * aus dem Vertrag abgeleitete {@link totalAmount} (wird NICHT überschrieben).
     */
    mahnRelevanterSaldoLabel: string | null = null;

    /**
     * G1/G2: Laufender Beitrag (monatlich) aus dem Entscheidungs-Kontext —
     * Summe der monatlichen Prämien aller aktiven Verträge. {@code null}, wenn
     * das Backend keinen verlässlichen Wert liefert (KEINE falsche 0 €).
     * Dies ist KEIN Rückstand, sondern die laufende künftige Belastung.
     */
    laufenderBeitragMonatlich: number | null = null;

    /** G1/G2: Formatiertes Label des laufenden Beitrags (null → Feld nicht rendern). */
    laufenderBeitragLabel: string | null = null;

    /**
     * G2: Künftige Gesamtbelastung pro Monat = Raten-Betrag + laufender Beitrag.
     * {@code null}, wenn der laufende Beitrag nicht ermittelbar ist (dann kann
     * nicht verlässlich summiert werden → Zeile entfällt).
     */
    gesamtbelastungMonatlich: number | null = null;

    /** G2: Formatiertes Label der künftigen Gesamtbelastung/Monat. */
    gesamtbelastungLabel: string | null = null;

    /**
     * G7: true, wenn der historische Start-Saldo (balanceOnStart) vom heutigen
     * mahn-relevanten Rückstand abweicht (cent-genauer Vergleich). Steuert eine
     * Warn-Message im Template.
     */
    startVsHeuteDivergiert: boolean = false;

    /**
     * G12: Soft-Flag — true, wenn die monatliche Gesamtbelastung (Rate +
     * laufender Beitrag) die Tragfähigkeits-Schwelle übersteigt. KEIN harter
     * Block; nur ein diskreter Hinweis zur Tragfähigkeits-Prüfung.
     */
    tragfaehigkeitWarn: boolean = false;

    /**
     * G12: Tragfähigkeits-Schwelle. Die monatliche Gesamtbelastung sollte das
     * {@code tragfaehigkeitFactor}-fache des reinen laufenden Beitrags nicht
     * übersteigen; andernfalls wird zur Tragfähigkeits-Prüfung geraten. Bewusst
     * konfigurierbar gehalten (keine harte Sperre).
     */
    readonly tragfaehigkeitFactor: number = 2;

    /** A4: Vertrags-/LV-Bezug, sofern aus dem Kontext/Input verfügbar. */
    contractReferenceLabel: string | null = null;

    /** A4: Vermittler-Hinweis, sofern aus dem Kontext/Input verfügbar. */
    brokerHintLabel: string | null = null;

    /** A2: true während der Entscheidungs-Kontext geladen wird. */
    contextLoading: boolean = false;

    /**
     * A1: Pflicht-Begründung für die Anlage der Ratenvereinbarung. Solange leer
     * (oder nur Whitespace), bleibt "Ratenvereinbarung anlegen" deaktiviert.
     */
    reason: string = '';

    /** A1: true, wenn die Pflicht-Begründung beim Absenden fehlt. */
    reasonInvalid: boolean = false;

    /**
     * G7: Raten-Gesamtbetrag = aktueller mahn-relevanter Rückstand zum Stichtag
     * (Heute-Sicht aus dem Entscheidungs-Kontext). Nur als Pre-Context-Fallback
     * wird er aus dem Dunning-DTO (balanceOnStart) abgeleitet.
     */
    totalAmount: number = 0;
    totalAmountLabel: string = '—';

    /** Aktiver ISO-Währungscode (aus dem Kontext, sonst EUR). */
    private currentCurrency: string = 'EUR';

    /** Vom Benutzer eingegebene Raten-Anzahl (2-12). */
    rateCount: number = 3;

    /** Auto-berechneter Raten-Betrag (Gesamtbetrag / Anzahl). */
    rateAmount: number = 0;

    /** Start-Datum fuer die erste Rate (default: heute + 1 Monat). */
    startDate: Date | null = null;

    /** Vom Benutzer ausgewaehltes Intervall. */
    selectedInterval: BdRateInterval = 'MONTHLY';

    /** Auswahl-Optionen fuer das Raten-Intervall. */
    intervalOptions: BdRateIntervalOption[] = [
        {label: 'monatlich',     value: 'MONTHLY'},
        {label: '14-tägig',      value: 'BIWEEKLY'},
        {label: 'wöchentlich',   value: 'WEEKLY'}
    ];

    /** Auto-generierte Vorschau der Raten (Index, Betrag, Faelligkeit). */
    installments: BdRateAgreementInstallment[] = [];

    /**
     * A3: Betrag der letzten Rate (cent-genau). Weicht vom Auto-Betrag
     * {@link rateAmount} ab, wenn der Gesamtbetrag nicht glatt teilbar ist.
     */
    lastRateAmount: number = 0;

    /**
     * A3: true, wenn die letzte Rate vom Auto-Raten-Betrag abweicht (Cent-Rest
     * in der letzten Rate). Steuert den erläuternden Hinweis im Template.
     */
    lastRateDiffers: boolean = false;

    /**
     * C18: Summe der angezeigten Raten (cent-genau). Muss exakt
     * {@link totalAmount} entsprechen — die Differenz (Cent-Rest) wird in die
     * LETZTE Rate gelegt.
     */
    installmentsSum: number = 0;

    /**
     * C18: true, wenn die Summe der Raten cent-genau dem Gesamtbetrag
     * entspricht (Plausibilität Rate × Laufzeit ≈ Saldo). Bei false wird im
     * Template eine Warnung eingeblendet.
     */
    plausibilityOk: boolean = true;

    /** Mindest- und Maximalwerte fuer die Raten-Anzahl. */
    readonly minRateCount: number = 2;
    readonly maxRateCount: number = 12;

    /** A2: Subscription für das Laden des Entscheidungs-Kontextes. */
    private decisionContextSubscription: Subscription | null = null;

    /** Subscription für den Anlage-Request. */
    private createSubscription: Subscription | null = null;

    constructor(
        private readonly paymentPlanService: DunningPaymentPlanService,
        private readonly decisionDataService: BdDecisionDataService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
        this.loadDecisionContext();
    }

    /**
     * Re-initialises the component when the dunning input is updated after
     * initial mount. ngComponentOutlet may set inputs after ngOnInit fires,
     * so we must react to changes here to pick up balanceOnStart.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dunning'] && !changes['dunning'].firstChange) {
            this.initComponent();
        }
        // A2: dunningId kann nach ngOnInit gesetzt werden (ngComponentOutlet) —
        // dann den Entscheidungs-Kontext (nach-)laden.
        if (changes['dunningId'] && !changes['dunningId'].firstChange) {
            this.loadDecisionContext();
        }
    }

    ngOnDestroy(): void {
        this.decisionContextSubscription?.unsubscribe();
        this.createSubscription?.unsubscribe();
    }

    /**
     * A2: Lädt den Entscheidungs-Kontext der Mahnung und befüllt damit den
     * Kontext-Kopf (Kunde, Mahn-Typ, aktive Stufe als Klartext) sowie A4
     * (Vertrags-/LV-Bezug, Vermittler-Hinweis). Es werden ausschließlich vom
     * Backend gelieferte Werte verwendet — fehlt ein Wert, bleibt das Feld auf
     * dem aus dem Input abgeleiteten Wert bzw. 'unbekannt'.
     */
    private loadDecisionContext(): void {
        const id = this.resolveContextDunningId();
        if (id == null) {
            return;
        }
        this.decisionContextSubscription?.unsubscribe();
        this.contextLoading = true;
        this.decisionContextSubscription = this.decisionDataService.getDecisionContext(id).subscribe({
            next: (context: BdDecisionContextDto) => {
                this.applyDecisionContext(context);
                this.contextLoading = false;
            },
            error: () => {
                // Kein Fantasiewert: vorhandene Input-Ableitungen bleiben bestehen.
                this.contextLoading = false;
            }
        });
    }

    /**
     * A2/A4/G7: Übernimmt die Klartext-Werte aus dem Entscheidungs-Kontext in
     * den Kopf des Dialogs.
     *
     * G7: Der Raten-Gesamtbetrag ({@link totalAmount}) wird hier auf den HEUTIGEN
     * mahn-relevanten Rückstand gesetzt (nicht den historischen balanceOnStart);
     * anschließend werden Label und Raten neu berechnet.
     *
     * G1/G2: Der laufende Beitrag (monatlich) wird übernommen und die künftige
     * Gesamtbelastung/Monat (= Rate + laufender Beitrag) abgeleitet.
     */
    private applyDecisionContext(context: BdDecisionContextDto): void {
        // A2: Entscheidungs-Kontext ist fuer Kunde/Stufe massgeblich (Backend-Klartext).
        this.contextApplied = true;
        if (context.customerName) {
            this.customerName = context.customerName;
        }
        const levelLabel = context.levelName || context.levelShortName;
        if (levelLabel) {
            this.activeLevelLabel = levelLabel;
        }
        // Mahn-Typ: Entscheidungs-Kontext (Backend-Klartext) hat Vorrang vor dem
        // duennen Worklist-Wert; faellt auf den aus dem Dunning abgeleiteten Wert
        // zurueck und sonst auf 'unbekannt'.
        this.dunningTypeName = context.dunningTypeName
            ?? this.readString(this.dunning, ['dunningTypeName', 'typeName'])
            ?? '—';
        this.currentCurrency = context.currency || 'EUR';
        // A2: mahn-relevanter Saldo nur als Information im Kopf.
        if (typeof context.mahnRelevanterSaldo === 'number') {
            this.mahnRelevanterSaldoLabel = this.formatCurrencyWith(
                context.mahnRelevanterSaldo,
                this.currentCurrency
            );
            // G7: Raten-Gesamtbetrag = HEUTIGER mahn-relevanter Rückstand zum
            // Stichtag (nicht der historische balanceOnStart). Der Plan basiert
            // damit auf der Heute-Sicht.
            this.totalAmount = context.mahnRelevanterSaldo;
            this.totalAmountLabel = this.formatCurrencyWith(this.totalAmount, this.currentCurrency);
            // G7: Divergenz zwischen historischem Start-Saldo und Heute-Stand.
            const startSaldo = this.resolveStartBalance(this.dunning);
            this.startVsHeuteDivergiert = startSaldo != null
                && Math.round(Math.abs(startSaldo) * 100) !== Math.round(context.mahnRelevanterSaldo * 100);
            this.recomputeInstallments();
        }
        // G1/G2: Laufender Beitrag (monatlich). Null → Feld nicht rendern (keine
        // falsche 0 €). Bei vorhandenem Wert: Label setzen analog zum Saldo.
        if (typeof context.laufenderBeitragMonatlich === 'number') {
            this.laufenderBeitragMonatlich = context.laufenderBeitragMonatlich;
            this.laufenderBeitragLabel = this.formatCurrencyWith(
                context.laufenderBeitragMonatlich,
                this.currentCurrency
            );
        } else {
            this.laufenderBeitragMonatlich = null;
            this.laufenderBeitragLabel = null;
        }
        // G2/G12: Gesamtbelastung + Tragfähigkeit neu ableiten.
        this.recomputeBurden();
        // A4: Vertrags-/LV-Bezug — führendes Merkmal ist das Abrechnungskonto.
        if (context.accountName) {
            this.contractReferenceLabel = context.accountName;
        }
        // A4: Vermittler-Hinweis — nur wenn das Backend einen Pfad-Klartext liefert.
        if (context.pathLabel) {
            this.brokerHintLabel = context.pathLabel;
        }
    }

    /**
     * Liefert die für den Kontext-Load zu verwendende Mahnungs-Id: bevorzugt den
     * expliziten {@link dunningId}-Input, sonst die Id aus dem Dunning-DTO.
     */
    private resolveContextDunningId(): number | null {
        if (typeof this.dunningId === 'number' && this.dunningId > 0) {
            return this.dunningId;
        }
        return this.resolveDunningId(this.dunning);
    }

    private initComponent(): void {
        // A2: Kunde/Stufe nur aus dem Input ableiten, solange der (massgebliche)
        // Entscheidungs-Kontext sie noch NICHT gesetzt hat — sonst wuerde ein spaet
        // feuerndes ngOnChanges die Backend-Klartextwerte mit '—' ueberschreiben.
        if (!this.contextApplied) {
            this.customerName = this.buildCustomerName(this.dunning);
            this.activeLevelLabel = this.readString(this.dunning, ['activeLevelShortName', 'activeLevelName']) ?? '—';
            // A2/Mahn-Typ: ebenfalls nur aus dem Input ableiten, solange der Entscheidungs-Kontext
            // (Backend-Klartext) noch nicht gesetzt hat — sonst klobbert spaetes ngOnChanges den Wert auf '—'.
            this.dunningTypeName = this.readString(this.dunning, ['dunningTypeName', 'typeName']) ?? '—';
        }
        this.totalAmount = this.resolveTotalAmount(this.dunning);
        this.totalAmountLabel = this.formatCurrency(this.totalAmount);
        this.startDate = this.computeDefaultStartDate();
        this.recomputeInstallments();
        this.initialized = true;
    }

    // ------------------------------------------------------------------ //
    // Actions
    // ------------------------------------------------------------------ //

    /**
     * Vom Benutzer geaenderte Eingaben triggern eine Neuberechnung der Raten.
     */
    onInputChange(): void {
        this.recomputeInstallments();
        // G2/G12: Raten-Betrag kann sich geändert haben → Belastung neu ableiten.
        this.recomputeBurden();
    }

    /**
     * A1: Setzt das Validierungs-Flag zurück, sobald der Benutzer in der
     * Begründung tippt.
     */
    onReasonChange(): void {
        if (this.reason && this.reason.trim().length > 0) {
            this.reasonInvalid = false;
        }
    }

    /**
     * A1: true, wenn die Pflicht-Begründung leer/whitespace ist. Steuert den
     * disabled-Zustand des "Ratenvereinbarung anlegen"-Buttons.
     */
    get reasonMissing(): boolean {
        return !this.reason || this.reason.trim().length === 0;
    }

    /**
     * Sendet die berechneten Raten an den Backend-Service und schliesst den
     * Dialog bei Erfolg. Bei Fehler wird die Meldung im Dialog angezeigt.
     */
    confirm(): void {
        // A1: Pflicht-Begründung — blockiert das Anlegen mit deutscher Meldung.
        if (this.reasonMissing) {
            this.reasonInvalid = true;
            this.errorMessage = 'Bitte geben Sie eine Begründung ein.';
            return;
        }
        this.reasonInvalid = false;

        if (this.rateCount < this.minRateCount || this.rateCount > this.maxRateCount) {
            this.errorMessage = `Raten-Anzahl muss zwischen ${this.minRateCount} und ${this.maxRateCount} liegen.`;
            return;
        }
        if (!this.startDate) {
            this.errorMessage = 'Bitte ein Start-Datum auswählen.';
            return;
        }
        if (this.totalAmount <= 0) {
            this.errorMessage = 'Kein gültiger Gesamtbetrag verfügbar.';
            return;
        }
        if (!this.installments || this.installments.length === 0) {
            this.errorMessage = 'Keine Raten berechnet. Bitte Eingaben prüfen.';
            return;
        }

        // Bevorzugt den expliziten dunningId-Input (vom Drawer durchgereicht),
        // sonst die Id aus dem Dunning-DTO.
        const dunningId = this.resolveContextDunningId();
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnung referenziert.';
            return;
        }

        const request: BdCreateVirtualPlanRequest = {
            dunningId: dunningId,
            rates: this.installments.map(i => i.amount),
            dueDates: this.installments.map(i => this.toIsoDate(i.dueDate)),
            intervalLabel: this.resolveIntervalLabel(this.selectedInterval),
            // A1: Pflicht-Begründung im Body mitsenden (Feldname 'reason').
            reason: this.reason.trim()
        };

        this.errorMessage = null;
        this.submitting = true;

        this.createSubscription?.unsubscribe();
        this.createSubscription = this.paymentPlanService.createVirtualPlan(request).subscribe({
            next: () => {
                this.submitting = false;
                this.closeDialog();
                this.actionCompleted.emit();
            },
            error: (err: unknown) => {
                this.submitting = false;
                this.errorMessage = this.extractError(err);
            }
        });
    }

    onCancel(): void {
        this.closeDialog();
        this.actionCancelled.emit();
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.actionCancelled.emit();
        }
    }

    // ------------------------------------------------------------------ //
    // Calculation helpers
    // ------------------------------------------------------------------ //

    /**
     * G10: Berechnet die Raten über den zustandslosen {@link computeRatePlan}-Util
     * (Mathematik UNVERÄNDERT in den Util gehoben) und überträgt das Ergebnis in
     * den Dialog-State.
     */
    private recomputeInstallments(): void {
        const count = Math.max(this.minRateCount, Math.min(this.maxRateCount, this.rateCount));
        const plan = computeRatePlan(this.totalAmount, count, this.startDate, this.selectedInterval);
        this.rateAmount = plan.rateAmount;
        this.lastRateAmount = plan.lastRateAmount;
        this.lastRateDiffers = plan.lastRateDiffers;
        this.installments = plan.installments;
        this.installmentsSum = plan.installmentsSum;
        this.plausibilityOk = plan.plausibilityOk;
    }

    /**
     * G2/G12: Leitet die künftige Gesamtbelastung/Monat (= Raten-Betrag +
     * laufender Beitrag) sowie das weiche Tragfähigkeits-Warn-Flag ab.
     *
     * Ist der laufende Beitrag nicht ermittelbar (null), kann nicht verlässlich
     * summiert werden → Gesamtbelastung entfällt (null) und es wird keine
     * Tragfähigkeits-Warnung gezeigt.
     */
    private recomputeBurden(): void {
        if (this.laufenderBeitragMonatlich == null) {
            this.gesamtbelastungMonatlich = null;
            this.gesamtbelastungLabel = null;
            this.tragfaehigkeitWarn = false;
            return;
        }
        const sum = this.roundCurrency(this.rateAmount + this.laufenderBeitragMonatlich);
        this.gesamtbelastungMonatlich = sum;
        this.gesamtbelastungLabel = this.formatCurrencyWith(sum, this.currentCurrency);
        // G12: Weiche Schwelle — Gesamtbelastung > Faktor × laufender Beitrag.
        // KEIN harter Block; nur ein Hinweis zur Tragfähigkeits-Prüfung.
        const schwelle = this.tragfaehigkeitFactor * this.laufenderBeitragMonatlich;
        this.tragfaehigkeitWarn = sum > schwelle;
    }

    private computeDefaultStartDate(): Date {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        return date;
    }

    private roundCurrency(value: number): number {
        return Math.round(value * 100) / 100;
    }

    private formatCurrency(value: number): string {
        return this.formatCurrencyWith(value, 'EUR');
    }

    /** Formatiert einen Betrag mit dem übergebenen ISO-Währungscode. */
    private formatCurrencyWith(value: number, currency: string): string {
        return value.toLocaleString('de-DE', {
            style: 'currency',
            currency: currency
        });
    }

    // ------------------------------------------------------------------ //
    // Template helpers
    // ------------------------------------------------------------------ //

    formatRateAmount(amount: number): string {
        return this.formatCurrency(amount);
    }

    /** C18: Formatierte Raten-Summe für die Summenzeile. */
    get installmentsSumLabel(): string {
        return this.formatCurrency(this.installmentsSum);
    }

    /**
     * G11: Fälligkeit der LETZTEN Rate für die kompakte Vorlese-Box. Null, wenn
     * (noch) keine Raten berechnet sind.
     */
    get lastDueDate(): Date | null {
        if (!this.installments || this.installments.length === 0) {
            return null;
        }
        return this.installments[this.installments.length - 1].dueDate;
    }

    // ------------------------------------------------------------------ //
    // Dunning helpers
    // ------------------------------------------------------------------ //

    private closeDialog(): void {
        this.visible = false;
    }

    private buildCustomerName(dunning: BdDunningDto | null): string {
        if (!dunning) {
            return '—';
        }
        const first = (dunning.personFirstName ?? dunning.firstName ?? '') as string;
        const last = (dunning.personLastName ?? dunning.lastName ?? '') as string;
        const combined = `${first} ${last}`.trim();
        return combined.length > 0 ? combined : '—';
    }

    /**
     * G7: PRE-CONTEXT-Fallback. Liefert den absoluten Start-Rückstand aus dem
     * Dunning-DTO. Wird NUR verwendet, solange der Entscheidungs-Kontext noch
     * nicht geladen ist (initComponent) bzw. wenn dessen Laden fehlschlägt.
     * Sobald der Kontext vorliegt, ist der HEUTIGE mahn-relevante Rückstand
     * maßgeblich (siehe {@link applyDecisionContext}).
     */
    private resolveTotalAmount(dunning: BdDunningDto | null): number {
        const startBalance = this.resolveStartBalance(dunning);
        // balanceOnStart (and sibling balance-like fields) is the NEGATIVE balance
        // at dunning start. The prefilled "Gesamtbetrag" must be the ABSOLUTE
        // outstanding amount, so return its magnitude.
        if (startBalance != null) {
            return Math.abs(startBalance);
        }
        return 0;
    }

    /**
     * G7: Liefert den historischen Start-Saldo (balanceOnStart bzw. verwandte
     * Saldo-Felder) als vorzeichenbehafteten Rohwert, oder {@code null}, wenn
     * kein nicht-nuller Wert verfügbar ist. Wird für die Divergenz-Prüfung
     * (Start-Saldo vs. heutiger Rückstand) und als Pre-Context-Fallback genutzt.
     */
    private resolveStartBalance(dunning: BdDunningDto | null): number | null {
        if (!dunning) {
            return null;
        }
        const candidate = (
            dunning['balanceOnStart']
            ?? dunning['currentBalance']
            ?? dunning['balance']
            ?? dunning['totalAmount']
        ) as unknown;
        if (typeof candidate === 'number' && candidate !== 0) {
            return candidate;
        }
        return null;
    }

    private readString(source: BdDunningDto | null, keys: string[]): string | null {
        if (!source) {
            return null;
        }
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        }
        return null;
    }

    // ------------------------------------------------------------------ //
    // Service-Call helpers
    // ------------------------------------------------------------------ //

    /**
     * Liefert die Mahnungs-ID aus dem Input-DTO, falls vorhanden.
     */
    private resolveDunningId(dunning: BdDunningDto | null): number | null {
        if (!dunning) {
            return null;
        }
        const candidate = (dunning['id'] ?? dunning['dunningId']) as unknown;
        if (typeof candidate === 'number' && candidate > 0) {
            return candidate;
        }
        return null;
    }

    /**
     * Mapped das interne Intervall-Enum auf das fuer Audit / Logging genutzte
     * deutsche Label.
     */
    private resolveIntervalLabel(interval: BdRateInterval): string {
        const option = this.intervalOptions.find(o => o.value === interval);
        return option ? option.label : interval;
    }

    /**
     * Formatiert ein Date-Objekt als ISO-Datum (yyyy-MM-dd) fuer das Backend.
     */
    private toIsoDate(date: Date): string {
        const yyyy = date.getFullYear();
        const mm = `${date.getMonth() + 1}`.padStart(2, '0');
        const dd = `${date.getDate()}`.padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    /**
     * Extrahiert eine lesbare Fehlermeldung aus einer HTTP-Error-Response.
     */
    private extractError(err: unknown): string {
        if (err && typeof err === 'object') {
            const anyErr = err as {error?: {message?: string}; message?: string};
            return anyErr.error?.message ?? anyErr.message ?? 'Unbekannter Fehler beim Anlegen der Ratenvereinbarung.';
        }
        return 'Unbekannter Fehler beim Anlegen der Ratenvereinbarung.';
    }
}
